import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import { useElapsedTimer } from "./useElapsedTimer";
import { submitModakRound } from "./modakApi";
import { RoundHeader } from "./RoundChrome";
import useIsNarrow from "./useIsNarrow";

// Hosted on S3 rather than bundled under public/modak-assets — sidesteps
// whatever was breaking playback in-app (see Round2 audio history) since
// these are served straight from S3, not through the app's own static host.
const R2_AUDIO_BASE = "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/modakmarathon";

const CLIPS = [
  {
    src: `${R2_AUDIO_BASE}/1.mp3`,
    correct: "Vighnahartha - Antim",
    options: [
      "Vighnahartha - Antim",
      "Ganpati Aarti - Sarkar 3",
      "Suno Ganpati Bappa Morya - Judwaa 2",
      "Morya Re - Don",
    ],
  },
  {
    src: `${R2_AUDIO_BASE}/2.mp3`,
    correct: "Gajanana - Bajirao mastani",
    options: [
      "Hey Ganaraya - ABCD 2",
      "Suno Ganpati Bappa Morya - Judwaa 2",
      "Gajanana - Bajirao mastani",
      "Sindoor lal Chadayo - Vaastav",
    ],
  },
  {
    src: `${R2_AUDIO_BASE}/3.mp3`,
    correct: "Deva Shree Ganesha - Agneepath",
    options: [
      "Aala Re Aala Ganesha - Daddy",
      "Danka Baja - Mumbai Saga",
      "Vighnahartha - Antim",
      "Deva Shree Ganesha - Agneepath",
    ],
  },
  {
    src: `${R2_AUDIO_BASE}/4.mp3`,
    correct: "Sadda Dil Vi Tu - ABCD",
    options: [
      "Sadda Dil Vi Tu - ABCD",
      "Sindoor lal Chadayo - Vaastav",
      "Vighnahartha - Antim",
      "Hey Ganaraya - ABCD 2",
    ],
  },
];

const RULES = ["Enable the audio on your browser", "Hear the audio and select the corresponding right answer"];

export default function Round2() {
  const navigate = useNavigate();
  const { loading, user, adminToken, timerKey, status } = useRoundGate(2);
  const time = useElapsedTimer(timerKey);
  const narrow = useIsNarrow();

  const [picked, setPicked] = useState([null, null, null, null]);
  const [locked, setLocked] = useState([false, false, false, false]);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [mobileIndex, setMobileIndex] = useState(0);
  const basePointsRef = useRef(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (status) {
      basePointsRef.current = status.points ?? 0;
      setScore(basePointsRef.current);
    }
  }, [status]);

  if (loading) return null;

  const pauseOthers = (el) => {
    document.querySelectorAll("audio").forEach((a) => {
      if (a !== el) a.pause();
    });
  };

  const choose = (clipIdx, option) => {
    if (locked[clipIdx]) return;
    const nextPicked = [...picked];
    nextPicked[clipIdx] = option;
    setPicked(nextPicked);

    if (option === CLIPS[clipIdx].correct) {
      const nextLocked = [...locked];
      nextLocked[clipIdx] = true;
      setLocked(nextLocked);
      setScore((s) => s + 1);
    }
  };

  const allAnswered = picked.every((p) => p != null);
  const visibleIndexes = narrow ? [mobileIndex] : CLIPS.map((_, i) => i);

  const finish = async (finalPoints) => {
    // Ref guard so a fast double-click on Submit/SKIP can't race two
    // finish() calls through before `submitting` state re-renders — see
    // Round1.jsx for the full explanation.
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    await submitModakRound(adminToken, {
      userId: user.userId,
      round: 2,
      points: finalPoints,
      time,
      answers: picked.map((p) => p ?? "").join(","),
      user,
    });
    navigate("/home");
  };

  const handleSubmit = () => {
    if (!allAnswered || submitting) return;
    finish(score);
  };

  const handleSkip = () => {
    if (submitting) return;
    finish(basePointsRef.current);
  };

  return (
    <ModakBackdrop>
      <div className="modak-round">
        <RoundHeader time={time} score={score} rules={RULES} onSkip={handleSkip} />

        <div className="modak-round-heading-row">
          <span className="modak-round-label">Music-o-logy</span>
          <div className="modak-round-instruction">Guess the Ganesh themed Movies from the songs</div>
        </div>

        <div className={narrow ? "modak-slider-track" : undefined}>
          {narrow && (
            <button
              type="button"
              className="modak-slider-side-arrow modak-slider-side-arrow--prev"
              onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
              disabled={mobileIndex === 0}
              aria-label="Previous question"
            >
              ‹
            </button>
          )}

          <div className="modak-r2-grid">
            {visibleIndexes.map((cIdx) => {
              const clip = CLIPS[cIdx];
              return (
                <div className="modak-r2-clip" key={cIdx}>
                  {/* `src` set directly on <audio> rather than via a nested <source> —
                      this app runs inside a native app's WebView (see
                      user.jsx's GameStatus bridge), and several embedded/hybrid
                      WebView engines fail to resolve <source> children (stuck at
                      0:00/0:00) while a direct src loads reliably. */}
                  <audio
                    controls
                    preload="metadata"
                    src={clip.src}
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    onPlay={(e) => pauseOthers(e.target)}
                  />
                  {clip.options.map((opt) => {
                    const isPicked = picked[cIdx] === opt;
                    const isCorrectPick = isPicked && opt === clip.correct;
                    const isWrongPick = isPicked && opt !== clip.correct;
                    return (
                      <label
                        key={opt}
                        className={`modak-r2-option${isCorrectPick ? " is-correct" : ""}${
                          isWrongPick ? " is-wrong" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name={`clip-${cIdx}`}
                          disabled={locked[cIdx]}
                          checked={isPicked}
                          onChange={() => choose(cIdx, opt)}
                        />
                        {opt}
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {narrow && (
            <button
              type="button"
              className="modak-slider-side-arrow modak-slider-side-arrow--next"
              onClick={() => setMobileIndex((i) => Math.min(CLIPS.length - 1, i + 1))}
              disabled={mobileIndex === CLIPS.length - 1}
              aria-label="Next question"
            >
              ›
            </button>
          )}
        </div>

        {narrow && (
          <div className="modak-slider-nav">
            <span className="modak-slider-counter">
              {mobileIndex + 1} / {CLIPS.length}
            </span>
          </div>
        )}

        <button type="button" className="modak-btn modak-submit-btn" disabled={!allAnswered || submitting} onClick={handleSubmit}>
          Submit
        </button>
      </div>
    </ModakBackdrop>
  );
}
